import { router, useFocusEffect } from 'expo-router';
import { useAtomValue } from 'jotai';
import React from 'react';
import {
    BodyMediumRegular,
    DEFAULT_COLOR,
    DEFAULT_ICON,
    Icon,
    type IconType,
    NextAction,
    ScrollView,
    Sheet,
    SPACE_COLORS,
    SPACE_ICONS,
    SPACE_SECONDARY_COLORS,
    View,
    YStack,
} from 'smoothie';

import { captureException } from '../../../logger/Logger';
import { type User } from '../../../user/types/User';
import {
    useDeclinePendingJointInvite,
    useGetPendingJointInvites,
} from '../../hooks/useGetJointInvites';
import { pendingJointInvitations } from '../../state/PendingJointInvitationsAtoms';
import type { PendingJointInvitation } from '../../types/PendingJointInvitations';

interface InvitationNoticeProps {
    invitation: PendingJointInvitation;
    onClose: () => void; // hacky prop to close the sheet that this lives in
    user: User;
}

const InvitationNotice = (props: InvitationNoticeProps) => {
    const { invitation, onClose, user } = props;
    const { refetch } = useGetPendingJointInvites();
    const { mutate: declineInvite } = useDeclinePendingJointInvite();

    const iconColor = SPACE_SECONDARY_COLORS[invitation.color ?? DEFAULT_COLOR];
    const iconBackground = SPACE_COLORS[invitation.color ?? DEFAULT_COLOR];
    const iconType = SPACE_ICONS[invitation.icon ?? DEFAULT_ICON] as IconType;

    return (
        <View marginTop={24}>
            <NextAction
                key={invitation.bankAccountId + invitation.nickname}
                title={invitation.nickname}
                description="You've been invited to be a joint account owner on a Fruitful Cash Account!"
                iconOverride={
                    <View
                        backgroundColor={iconBackground}
                        padding={8}
                        borderRadius={12}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Icon icon={iconType} stringColor={iconColor} />
                    </View>
                }
                primaryAction={{
                    text: 'Accept',
                    onPress: () => {
                        onClose();
                        router.push(
                            `/banking/accept-cash-invite?userId=${user.id}&bankAccountId=${invitation.bankAccountId}`,
                        );
                    },
                }}
                secondaryAction={{
                    text: 'Decline',
                    onPress: () => {
                        declineInvite(
                            {
                                body: {
                                    bankAccountId: invitation.bankAccountId,
                                },
                            },
                            {
                                onSuccess: () => {
                                    void refetch();
                                },
                                onError: (error) => {
                                    captureException(error);
                                    onClose();
                                },
                            },
                        );
                    },
                }}
            />
        </View>
    );
};

type ScrollViewRef = ScrollView & {
    flashScrollIndicators: () => void;
};

interface PendingInvitationsProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
}

const PendingInvitations = ({
    isOpen,
    onClose,
    user,
}: PendingInvitationsProps) => {
    const pendingInvitations = useAtomValue(pendingJointInvitations);
    const scrollViewRef = React.useRef<ScrollViewRef | null>(null);

    useFocusEffect(() => {
        setTimeout(() => {
            scrollViewRef.current?.flashScrollIndicators();
        }, 500);
    });

    const nonMoneyMapInvitations = pendingInvitations.filter(
        (invite) => !invite.fromMoneyMap,
    );

    // fromMoneyMap here really means "from a money map that a member can take action on"
    // (ie status is ACTIVE and there are no newer PREVIEW money maps)
    const hasMoneyMapInvitations = pendingInvitations.some(
        (invite) => invite.fromMoneyMap,
    );

    const noInvitations = (
        <BodyMediumRegular margin={24} key="no-invitations">
            No pending invitations at this time
        </BodyMediumRegular>
    );

    const invitations = nonMoneyMapInvitations.map((invitation) => {
        const { bankAccountId } = invitation;

        return (
            <InvitationNotice
                key={bankAccountId}
                invitation={invitation}
                onClose={onClose}
                user={user}
            />
        );
    });

    if (hasMoneyMapInvitations) {
        invitations.unshift(
            <NextAction
                key="money-map-accounts"
                title="New Money Map accounts"
                description="You can now review your Money Map and join your new accounts!"
                iconOverride={
                    <View
                        backgroundColor={SPACE_SECONDARY_COLORS.GREEN}
                        padding={8}
                        borderRadius={12}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Icon
                            icon={SPACE_ICONS.fruitful as IconType}
                            stringColor={SPACE_COLORS.GREEN}
                        />
                    </View>
                }
                primaryAction={{
                    text: 'Set up your Money Map',
                    onPress: () => {
                        onClose();
                        router.push('/home/roadmap');
                    },
                }}
            />,
        );
    }

    if (invitations.length <= 0) {
        invitations.push(noInvitations);
    }

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            disableDrag
            title="Invitations"
            showCloseButton
        >
            <YStack>
                <ScrollView
                    ref={scrollViewRef}
                    maxHeight={700}
                    $mobile_mini={{ maxHeight: 500, minHeight: 300 }}
                >
                    {invitations}
                </ScrollView>
            </YStack>
        </Sheet>
    );
};

export default PendingInvitations;
